import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Dropdown, message, Tooltip } from "antd";
import type { MenuProps } from "antd";
import cx from "classnames";
import styles from "./index.module.less";
import wsuiApi from "@api/wsuiApi";
import type { ISession, IGetSessionsParams } from "@typing/wsui/getSessions";
import { UserRole } from "@typing/wsui/userMe";
import { useChatStore } from "@store/chatStore";
import { useUserStore } from "@store/userStore";
import { useVerifyModalStore } from "@store/verifyModalStore";
import { usePassport } from "@hooks/usePassport";
import { receptionPath, workBench } from "@config/path";
import ConfirmModal from "@components/ConfirmModal";
import { Icon } from "@config/image";
import { useConfigStore } from "@store/configStore";

let receptionArrow = "https://muzhi-public-pic.cdn.bcebos.com/100115321294";
let avatar = "https://med-fe.cdn.bcebos.com/gh-fe/portrait.png";

interface IHistoryItem {
    id: string;
    title: string;
}

interface ISidebarProps {
    className?: string;
    collapsed?: boolean;
    onCollapse?: (collapsed: boolean) => void;
}

/**
 * 会话列表管理 Hook
 * 提供会话列表的获取、状态管理和选中功能
 * 支持下拉分页加载（通过 offset 游标翻页）
 */
const useSessions = () => {
    const [selectedId, setSelectedId] = useState<string>("");
    const [historyList, setHistoryList] = useState<IHistoryItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    // 分页相关状态
    const lastSessionIdRef = useRef<string>("");
    const hasMoreRef = useRef<boolean>(true);
    const loadingMoreRef = useRef<boolean>(false);
    // 列表容器 ref，用于判断内容是否超出可视区
    const listRef = useRef<HTMLUListElement>(null);

    /**
     * 判断列表是否已填满可视区（即可以滚动）
     */
    const isListScrollable = useCallback(() => {
        const el = listRef.current;
        if (!el) return true; // 拿不到 DOM 时保守处理，不再自动加载
        return el.scrollHeight > el.clientHeight;
    }, []);

    /**
     * 加载会话列表
     * @param reset true = 首次加载/刷新（重置分页、替换列表、显示全屏 loading）
     *              false（默认）= 追加加载（触底或可视区未填满时触发）
     *
     * 每页加载完后检查列表是否可滚动：
     *   - 可滚动 → 停止，等用户触底再触发
     *   - 不可滚动 + has_more=true → 继续加载下一页，直到填满或无更多数据
     */
    const loadSessions = useCallback(
        async (reset = false) => {
            if (reset) {
                lastSessionIdRef.current = "";
                hasMoreRef.current = true;
            }

            if (!hasMoreRef.current || loadingMoreRef.current) return;

            loadingMoreRef.current = true;
            if (reset) setLoading(true);

            try {
                const params: IGetSessionsParams = {
                    viewType: "next",
                    size: 15,
                    appKey: "pc_wenzhen",
                    businessType: "doctor_youyi",
                };
                if (!reset && lastSessionIdRef.current) {
                    params.sessionId = lastSessionIdRef.current;
                }
                const response = await wsuiApi.getSessions(params);

                if (response.errno === 0 && response.data?.list) {
                    const newItems = response.data.list.map((s: ISession) => ({
                        id: s.sessionId,
                        title: s.firstMsgText,
                    }));
                    // 将服务端标题写入 store，供 chat 页头部直接读取（避免重命名后刷新标题不一致）
                    const { setPageTitle } = useChatStore.getState();
                    response.data?.list.forEach((s: ISession) => {
                        if (s?.firstMsgText) setPageTitle(s.sessionId, s.firstMsgText);
                    });
                    setHistoryList((prev) => (reset ? newItems : [...prev, ...newItems]));
                    if (response.data.list.length > 0) {
                        lastSessionIdRef.current = response.data.list[response.data.list.length - 1].sessionId;
                    }
                    hasMoreRef.current = response.data.hasMore ?? false;
                } else {
                    if (reset) setHistoryList([]);
                    hasMoreRef.current = false;
                }
            } catch (error) {
                console.error(reset ? "获取历史会话列表失败:" : "加载更多会话失败:", error);
                hasMoreRef.current = false;
            } finally {
                loadingMoreRef.current = false;
                if (reset) setLoading(false);
            }

            // DOM 更新是异步的，用 setTimeout 等下一帧再测量
            if (hasMoreRef.current) {
                setTimeout(() => {
                    if (!isListScrollable()) {
                        loadSessions();
                    }
                }, 0);
            }
        },
        [isListScrollable]
    );

    // 组件挂载时获取历史记录
    useEffect(() => {
        loadSessions(true);
    }, [loadSessions]);

    const fetchSessions = useCallback(() => loadSessions(true), [loadSessions]);

    return {
        selectedId,
        setSelectedId,
        historyList,
        setHistoryList,
        loading,
        fetchSessions,
        loadMoreSessions: loadSessions,
        listRef,
    };
};

const Sidebar: React.FC<ISidebarProps> = ({ className, collapsed = false, onCollapse }) => {
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId?: string }>();
    const { showVerifyModal } = useVerifyModalStore();
    // 会话编辑相关状态
    const [renameModalVisible, setRenameModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [logoutModalVisible, setLogoutModalVisible] = useState(false);
    const [editingSession, setEditingSession] = useState<{ id: string; title: string } | null>(null);
    const [operationLoading, setOperationLoading] = useState(false);
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    // 展开/收起按钮的Tooltip显示状态
    const [collapseTooltipOpen, setCollapseTooltipOpen] = useState(false);
    const collapseTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // 退出登录下拉菜单跟随鼠标位置
    const [logoutMousePos, setLogoutMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const logoutDropdownVisibleRef = useRef(false);
    const {
        newSessionCreated,
        clearNewSessionNotification,
        sessionTitleUpdated,
        clearSessionTitleNotification,
        sseCompleted,
        clearSSECompletedNotification,
        notifySessionTitleUpdate,
    } = useChatStore();
    const {
        selectedId,
        setSelectedId,
        historyList,
        setHistoryList,
        loading: sessionsLoading,
        fetchSessions,
        loadMoreSessions,
        listRef,
    } = useSessions();
    const { userInfo, loading: userLoading, fetchUserInfo } = useUserStore();
    const { loaded: passportLoaded, loading: passportLoading, showLogin, logout } = usePassport();

    // 组件挂载时获取用户信息
    useEffect(() => {
        if (!userInfo) {
            fetchUserInfo();
        }
    }, [userInfo, fetchUserInfo]);

    // 登录成功后刷新历史会话列表
    const { isLogin } = useUserStore();
    useEffect(() => {
        if (isLogin) {
            fetchSessions();
        }
    }, [isLogin, fetchSessions]);

    const handleShowLogin = useCallback(() => {
        showLogin(() => {
            message.success("登录成功");
            setTimeout(() => {
                navigate(workBench.home);
            }, 150);
        });
    }, [showLogin, navigate]);

    /**
     * 根据路由参数同步选中状态
     */
    useEffect(() => {
        if (!sessionId) {
            setSelectedId("");
        }

        if (sessionId && sessionId !== selectedId) {
            setSelectedId(sessionId);
        }
    }, [sessionId, selectedId, setSelectedId]);

    /**
     * 处理新建问答点击
     */
    const handleNewQuestion = () => {
        const { resetCoreSession } = useChatStore.getState();
        resetCoreSession();
        useConfigStore.getState().clearConfig();
        navigate("/workbench/web/main");
    };

    /**
     * 处理历史记录项点击
     * @param id 记录ID
     */
    const handleHistoryClick = (id: string) => {
        navigate(`/workbench/web/chat/${id}`);
    };

    /**
     * 处理接诊台跳转
     * -1 未登录：新开浏览器页，跳转到工作台path登录页
     * 0 已登录未认证：显示认证引导弹窗
     * >0 已登录已认证：跳转到医生工作台页
     */
    const handleGotoReception = () => {
        const role = userInfo?.role as UserRole | undefined;
        // 未登录
        if (role === -1) {
            window.open(receptionPath.login, "_blank");
        }
        // 已登录未认证
        else if (role === 0) {
            showVerifyModal();
        } else {
            window.open(receptionPath.home, "_blank");
        }
    };

    /**
     * 监听新会话创建
     */
    useEffect(() => {
        if (newSessionCreated) {
            console.log("收到新会话通知:", newSessionCreated);

            // 在列表顶部插入新会话（临时标题）
            const newSession: IHistoryItem = {
                id: newSessionCreated.sessionId,
                title: "新会话", // 临时标题，后续会被真实标题替换
            };

            // 检查是否已存在该会话，避免重复添加
            setHistoryList((prev) => {
                const exists = prev.some((item) => item.id === newSession.id);
                if (exists) {
                    return prev;
                }
                return [newSession, ...prev];
            });

            // 选中新会话
            setSelectedId(newSessionCreated.sessionId);

            // 清除通知
            clearNewSessionNotification();
        }
    }, [newSessionCreated, setHistoryList, setSelectedId, clearNewSessionNotification]);

    /**
     * 监听会话标题更新
     */
    useEffect(() => {
        if (sessionTitleUpdated) {
            console.log("收到标题更新通知:", sessionTitleUpdated);

            // 更新对应会话的标题
            setHistoryList((prev) =>
                prev.map((item) =>
                    item.id === sessionTitleUpdated.sessionId ? { ...item, title: sessionTitleUpdated.title } : item
                )
            );

            // 清除通知
            clearSessionTitleNotification();
        }
    }, [sessionTitleUpdated, setHistoryList, clearSessionTitleNotification]);

    /**
     * 监听 SSE 对话完成
     * 对话完成后刷新历史列表（因为顺序可能变化）
     */
    useEffect(() => {
        if (sseCompleted) {
            // 刷新历史列表
            fetchSessions();

            // 清除通知
            clearSSECompletedNotification();
        }
    }, [sseCompleted, fetchSessions, clearSSECompletedNotification]);

    /**
     * 打开重命名弹窗
     */
    const handleOpenRenameModal = useCallback((item: IHistoryItem, e: React.MouseEvent) => {
        // 阻止事件冒泡，避免触发列表项的点击
        e.stopPropagation();
        setEditingSession({ id: item.id, title: item.title });
        setRenameModalVisible(true);
    }, []);

    /**
     * 打开删除确认弹窗
     */
    const handleOpenDeleteModal = useCallback((item: IHistoryItem, e: React.MouseEvent) => {
        // 阻止事件冒泡，避免触发列表项的点击
        e.stopPropagation();
        setEditingSession({ id: item.id, title: item.title });
        setDeleteModalVisible(true);
    }, []);

    /**
     * 确认重命名
     */
    const handleConfirmRename = useCallback(
        async (value?: string) => {
            if (!editingSession || !value?.trim()) return;
            const len = value?.trim().length;
            if (len > 500) {
                message.error("会话标题长度不能超过500个字符");
                return;
            }

            setOperationLoading(true);
            try {
                const { errno, errmsg } = await wsuiApi.updateSession({
                    sessionId: editingSession.id,
                    sessionTitle: value.trim(),
                    appKey: "pc_wenzhen",
                    businessType: "doctor_youyi",
                });

                if (errno === 0) {
                    setHistoryList((prev) =>
                        prev.map((item) => (item.id === editingSession.id ? { ...item, title: value.trim() } : item))
                    );
                    // 直接更新 store 中的 pageTitle
                    const { setPageTitle } = useChatStore.getState();
                    setPageTitle(editingSession.id, value.trim());

                    notifySessionTitleUpdate(editingSession.id, value.trim());
                    message.success("修改成功");
                    setRenameModalVisible(false);
                } else {
                    message.error(errmsg || "修改失败");
                }
            } catch (error) {
                console.error("重命名失败:", error);
                message.error("修改失败，请稍后重试");
            } finally {
                setOperationLoading(false);
            }
        },
        [editingSession, setHistoryList, notifySessionTitleUpdate]
    );

    /**
     * 确认删除
     */
    const handleConfirmDelete = useCallback(async () => {
        if (!editingSession) return;

        setOperationLoading(true);
        try {
            const { errno, errmsg } = await wsuiApi.deleteSession({
                sessionId: editingSession.id,
                appKey: "pc_wenzhen",
                businessType: "doctor_youyi",
            });

            if (errno === 0) {
                // 从列表里面移除
                setHistoryList((prev) => prev.filter((item) => item.id !== editingSession.id));
                message.success("删除成功");
                setDeleteModalVisible(false);

                // 只有当删除的是当前选中的会话时，才跳转到首页 删除非当前会话时，保持原页面不变
                if (sessionId && sessionId === editingSession.id) {
                    // 清空 coreSessionId，确保 main 页重新请求 initCoreData
                    useChatStore.getState().resetCoreSession();
                    navigate("/workbench/web/main");
                }
            } else {
                message.error(errmsg || "删除失败");
            }
        } catch (error) {
            console.error("删除失败:", error);
            message.error("删除失败，请稍后重试");
        } finally {
            setOperationLoading(false);
        }
    }, [editingSession, sessionId, navigate, setHistoryList]);

    /**
     * 处理历史列表滚动 — 触底时加载下一页
     */
    const handleHistoryScroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        // 距底部 50px 内触发加载
        if (scrollHeight - scrollTop - clientHeight < 50) {
            loadMoreSessions();
        }
    }, [loadMoreSessions]);

    /**
     * 处理下拉框打开/关闭状态变化
     */
    const handleDropdownOpenChange = useCallback((open: boolean, itemId: string) => {
        setOpenDropdownId(open ? itemId : null);
    }, []);

    /**
     * 生成下拉菜单项
     */
    const getDropdownItems = useCallback(
        (item: IHistoryItem): MenuProps["items"] => {
            return [
                {
                    key: "rename",
                    label: "重命名",
                    icon: <img src={Icon.edit} className={styles.icon} />,
                    onClick: (e) => handleOpenRenameModal(item, e.domEvent as React.MouseEvent),
                },
                {
                    key: "delete",
                    label: "删除",
                    icon: <img src={Icon.delete} className={styles.icon} />,
                    className: styles.deleteItem,
                    onClick: (e) => handleOpenDeleteModal(item, e.domEvent as React.MouseEvent),
                },
            ];
        },
        [handleOpenRenameModal, handleOpenDeleteModal]
    );

    return (
        <nav className={cx(styles.sidebar, { [styles.collapsed]: collapsed }, className)}>
            {/* Logo区域 */}
            <header className={cx(styles.logoSection, { [styles.collapsed]: collapsed })}>
                <div className={styles.logoContainer} onClick={handleNewQuestion} style={{ cursor: 'pointer' }}>
                    {!collapsed && (
                        // 展开状态：显示完整Logo
                        <div className={styles.logoWrapper}>
                            <img src="https://muzhi-public-pic.cdn.bcebos.com/100115321291" alt="" />
                        </div>
                    )}
                </div>
                <div
                    onClick={() => {
                        // 清除定时器并关闭 tooltip
                        if (collapseTooltipTimerRef.current) {
                            clearTimeout(collapseTooltipTimerRef.current);
                            collapseTooltipTimerRef.current = null;
                        }
                        setCollapseTooltipOpen(false);
                        // 使用 setTimeout 确保 tooltip 在状态变化前已关闭
                        setTimeout(() => {
                            onCollapse?.(!collapsed);
                        }, 50);
                    }}
                    onMouseEnter={() => {
                        // 延迟 1 秒显示 tooltip
                        collapseTooltipTimerRef.current = setTimeout(() => {
                            setCollapseTooltipOpen(true);
                        }, 1000);
                    }}
                    onMouseLeave={() => {
                        // 清除定时器并关闭 tooltip
                        if (collapseTooltipTimerRef.current) {
                            clearTimeout(collapseTooltipTimerRef.current);
                            collapseTooltipTimerRef.current = null;
                        }
                        setCollapseTooltipOpen(false);
                    }}
                    aria-label={collapsed ? "展开侧边栏" : "收起侧边栏"}
                    aria-expanded={!collapsed}
                >
                    <Tooltip
                        title={collapsed ? "展开边栏" : "收起边栏"}
                        placement="bottom"
                        arrow={false}
                        color="#fff"
                        open={collapseTooltipOpen}
                    >
                        <div className={styles.iconContainer}>
                            <img src={collapsed ? Icon.expand : Icon.collapse} className={styles.collapseIcon} alt="" />
                        </div>
                    </Tooltip>
                </div>
            </header>

            {/* 内容区域 */}
            <div className={styles.contentSection}>
                {/* 新建问答按钮 */}
                <div className={styles.newQuestionWrapper}>
                    {collapsed ? (
                        <Tooltip title="新建问答" placement="bottom" arrow={false} color="#fff" mouseEnterDelay={1}>
                            <div className={styles.newWrapper} onClick={handleNewQuestion}>
                                <img src={Icon.newQuestion} className={styles.newQuestionIcon} alt="" />
                            </div>
                        </Tooltip>
                    ) : (
                        <button className={cx(styles.newQuestionBtn)} onClick={handleNewQuestion}>
                            <img src={Icon.newQuestion} alt="" className={styles.btnIcon} />
                            {<span className={styles.btnText}>新建问答</span>}
                        </button>
                    )}
                </div>

                {/* 问答记录区域（收起状态下完全隐藏） */}
                {!collapsed && (
                    <>
                        <div className={styles.historyWrapper}>
                            <div className={styles.historyTitle}>问答记录</div>
                            <ul className={styles.historyList} ref={listRef} onScroll={handleHistoryScroll}>
                                {historyList.map((item) => (
                                    <li
                                        key={item.id}
                                        className={cx(styles.historyItem, selectedId === item.id && styles.active)}
                                        onClick={() => handleHistoryClick(item.id)}
                                    >
                                        <span className={cx(styles.historyItemTitle, "ellipsis")}>{item.title}</span>
                                        <Dropdown
                                            menu={{ items: getDropdownItems(item) }}
                                            trigger={["click"]}
                                            placement="bottomRight"
                                            classNames={{ root: styles.sessionDropdown }}
                                            onOpenChange={(open) => handleDropdownOpenChange(open, item.id)}
                                        >
                                            <span
                                                className={cx(styles.moreBtn, {
                                                    [styles.visible]: openDropdownId === item.id,
                                                })}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <img src={Icon.dot} alt="" style={{width: 16}}/>
                                            </span>
                                        </Dropdown>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
            {/* 底部区域 */}
            <footer className={styles.footerSection}>
                {/* 接诊台入口 */}
                <div
                    onClick={handleGotoReception}
                    className={cx(styles.receptionCard, { [styles.collapsed]: collapsed })}
                >
                    {collapsed ? (
                        <Tooltip title="去接诊台" placement="top" arrow={false} color="#fff" mouseEnterDelay={1}>
                            <div className={styles.receptionCollapsedInner}>
                                <img src={Icon.muzhiPc} alt="接诊台" className={styles.receptionIconCollapsed} />
                                <div className={styles.receptionTextCollapsed}>接诊台</div>
                            </div>
                        </Tooltip>
                    ) : (
                        // 展开状态：显示完整内容
                        <>
                            <div className={styles.receptionContent}>
                                <div className={styles.receptionTitle}>接诊台</div>
                                <div className={styles.receptionDesc}>前往原问诊接诊台</div>
                            </div>
                            <div className={styles.receptionArrowWrapper}>
                                <img src={receptionArrow} alt="" className={styles.receptionArrow} />
                            </div>
                        </>
                    )}
                </div>

                {/* 用户信息 - 根据登录状态渲染不同内容 */}
                {userInfo && userInfo.role !== UserRole.Unauthenticated ? (
                    // 已登录状态：显示头像和退出登录下拉
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: "logout",
                                    label: "退出登录",
                                    icon: <img src={Icon.logout} alt="" className={styles.logoutIcon} />,
                                    onClick: () => setLogoutModalVisible(true),
                                    title: "",
                                },
                            ],
                        }}
                        trigger={["hover"]}
                        mouseEnterDelay={1}
                        placement={collapsed ? undefined : "bottomRight"}
                        classNames={{ root: cx(styles.logoutDropdown, { [styles.collapsedDrop]: collapsed }) }}
                        popupRender={(menu) => (
                            <div 
                                style={!collapsed ? { 
                                    position: 'fixed',
                                    left: logoutMousePos.x + 10,
                                    top: logoutMousePos.y - 40,
                                } : undefined}
                            >
                                {menu}
                            </div>
                        )}
                        onOpenChange={(visible) => {
                            logoutDropdownVisibleRef.current = visible;
                        }}
                    >
                        <div 
                            className={collapsed ? styles.userInfoCollapsed : styles.userInfo}
                            title=""
                            onMouseMove={!collapsed ? (e) => {
                                if (!logoutDropdownVisibleRef.current) {
                                    setLogoutMousePos({ x: e.clientX, y: e.clientY });
                                }
                            } : undefined}
                        >
                            <div className={styles.avatarWrapper}>
                                <img
                                    src={userInfo.avatar || avatar}
                                    alt=""
                                    className={styles.avatar}
                                />
                            </div>
                            {!collapsed && <span className={styles.userName}>{userInfo.name || "医生"}</span>}
                        </div>
                    </Dropdown>
                ) : (
                    // 未登录状态：显示登录按钮
                    <>
                        {collapsed ? (
                            // 收起状态：使用Dropdown显示登录选项
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: "login",
                                            label: "登录",
                                            icon: <img src={Icon.login} alt="" className={styles.logoutIcon} />,
                                            onClick: passportLoaded ? handleShowLogin : undefined,
                                            title: "",
                                        },
                                    ],
                                }}
                                trigger={["hover"]}
                                mouseEnterDelay={1}
                                classNames={{ root: cx(styles.logoutDropdown, styles.collapsedDrop) }}
                            >
                                <div
                                    className={cx(
                                        styles.userInfoCollapsed,
                                        styles.loginArea,
                                        { [styles.disabled]: passportLoading || !passportLoaded }
                                    )}
                                    onClick={passportLoaded ? handleShowLogin : undefined}
                                >
                                    <div className={styles.avatarWrapper}>
                                        <img src={avatar} alt="" className={styles.avatar} />
                                    </div>
                                </div>
                            </Dropdown>
                        ) : (
                            // 展开状态：正常显示
                            <div
                                className={cx(
                                    styles.userInfo,
                                    styles.loginArea,
                                    { [styles.disabled]: passportLoading || !passportLoaded }
                                )}
                                onClick={passportLoaded ? handleShowLogin : undefined}
                            >
                                <div className={styles.avatarWrapper}>
                                    <img src={avatar} alt="" className={styles.avatar} />
                                </div>
                                <span className={styles.loginText}>登录</span>
                            </div>
                        )}
                    </>
                )}
            </footer>

            {/* 重命名弹窗 */}
            <ConfirmModal
                open={renameModalVisible}
                type="input"
                title="编辑对话名称"
                inputProps={{
                    value: editingSession?.title,
                    maxLength: 20,
                    placeholder: "请输入会话名称",
                }}
                confirmLoading={operationLoading}
                onOk={handleConfirmRename}
                onCancel={() => setRenameModalVisible(false)}
            />

            {/* 删除确认弹窗 */}
            <ConfirmModal
                open={deleteModalVisible}
                type="confirm"
                title="温馨提示"
                content="是否确认删除该对话记录，删除后将无法恢复记录"
                confirmLoading={operationLoading}
                onOk={handleConfirmDelete}
                onCancel={() => setDeleteModalVisible(false)}
            />

            {/* 退出登录确认弹窗 */}
            <ConfirmModal
                open={logoutModalVisible}
                type="warning"
                title="确认退出登录"
                content="退出登录不会丢失任何数据，你仍可以登录此账号"
                okText="退出登录"
                onOk={() => {
                    logout();
                    setLogoutModalVisible(false);
                }}
                onCancel={() => setLogoutModalVisible(false)}
            />
        </nav>
    );
};

export default Sidebar;
